import { ExperienceSection } from "@/types/experience-section";
import EventCard from "@/components/event/EventCardNew";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Inbox } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useMembershipDiscountStatus, getBestMembershipDiscount } from "@/hooks/useMembershipDiscountStatus";
import SeriesInterestButton from "@/components/experience/SeriesInterestButton";
import { useHostProfiles, mergeHostsIntoEvents } from "@/hooks/useHostProfiles";
import { useEventGroupAvailability } from "@/hooks/useEventGroupAvailability";

type DynamicEventSectionProps = {
  section: ExperienceSection;
  userId?: string;
  bookedEventIds?: string[];
  adventPassEligibility?: any;
  adventPassData?: any;
  isFromBingo?: boolean;
  isAdmin?: boolean;
  showSeparator?: boolean;
  onEditEvent?: (event: any) => void;
};

const DynamicEventSection = ({
  section,
  userId,
  bookedEventIds = [],
  adventPassEligibility,
  adventPassData,
  isFromBingo = false,
  isAdmin = false,
  showSeparator = false,
  onEditEvent,
}: DynamicEventSectionProps) => {
  const { data: memberDiscounts } = useMembershipDiscountStatus();
  
  const { data: events, isLoading } = useQuery({
    queryKey: ["section-events", section.id, section.section_type, section.tag, userId],
    queryFn: async () => {
      if (section.section_type === "archived") {
        // Fetch archived events
        const { data, error } = await supabase
          .from("events")
          .select("*")
          .eq("is_archived", true)
          .eq("is_canceled", false)
          .order("start_time", { ascending: false })
          .limit(3);

        if (error) throw error;
        return data || [];
      }


      if (section.section_type === "tag_based" && section.tag) {
        // Step 1: Fetch events filtered by the section tag
        const { data, error } = await supabase
          .from("events")
          .select(`id, title, description, venue, image_url, start_time, end_time, capacity, price, currency, whatsapp_group, host, host_id, secondary_host_id, credit_cost, latitude, longitude, agenda_text, is_archived, is_canceled, canceled_reason, allowed_booking_methods, required_tags, timezone, event_tags!inner(tag)`)
          .eq("event_tags.tag", section.tag)
          .eq("is_archived", false)
          .eq("is_canceled", false)
          .or(`end_time.gte.${new Date().toISOString()},and(end_time.is.null,start_time.gte.${new Date().toISOString()})`)
          .order("start_time", { ascending: true });

        if (error) throw error;
        if (!data || data.length === 0) return [];

        // Step 2: Fetch ALL tags for the returned events
        const eventIds = data.map(e => e.id);
        const { data: allTags, error: tagsError } = await supabase
          .from("event_tags")
          .select("event_id, tag")
          .in("event_id", eventIds);

        if (tagsError) throw tagsError;

        // Step 3: Merge full tag lists onto each event
        const tagsByEvent = new Map<string, string[]>();
        for (const t of allTags || []) {
          if (!t.event_id) continue;
          const list = tagsByEvent.get(t.event_id) || [];
          list.push(t.tag);
          tagsByEvent.set(t.event_id, list);
        }

        return data.map(event => ({
          ...event,
          tags: tagsByEvent.get(event.id) || []
        }));
      }

      return [];
    },
    enabled: !isFromBingo || section.section_type === "tag_based",
  });

  const { data: hostMap } = useHostProfiles(
    (events ?? []).flatMap((e: any) => [e.host_id, e.secondary_host_id])
  );
  const eventsWithHosts = mergeHostsIntoEvents(events as any[], hostMap);
  const { byEvent: groupsByEvent } = useEventGroupAvailability((events ?? []).map((event) => event.id));

  // Don't render archived sections when coming from bingo
  if (isFromBingo && section.section_type === "archived") {
    return null;
  }

  const isArchived = section.section_type === "archived";
  const isEmpty = !isLoading && (!events || events.length === 0);

  // For non-admin users, hide empty sections entirely
  if (isEmpty && !isAdmin) {
    return null;
  }

  return (
    <>
      {showSeparator && <Separator className="my-6" />}
      <div className="space-y-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="min-w-0 text-xl font-semibold leading-tight text-foreground">
            {section.title}
          </h2>
          <SeriesInterestButton section={section} />
        </div>
        {section.subtitle && (
          <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
            {section.subtitle}
          </p>
        )}
      </div>
      
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex min-h-44 overflow-hidden rounded-lg bg-card shadow-md">
              <div className="w-28 shrink-0 animate-pulse bg-muted sm:w-40" />
              <div className="min-w-0 flex-1 space-y-3 p-4 sm:p-5">
                <div className="h-5 w-2/3 animate-pulse rounded bg-muted" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : isEmpty ? (
        // Empty state placeholder for admins
        <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg py-8 px-4 flex flex-col items-center justify-center text-muted-foreground">
          <Inbox className="h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm font-medium">No events in this section</p>
          <p className="text-xs mt-1">
            {section.section_type === "tag_based" && section.tag 
              ? `No upcoming events with "${section.tag}" tag`
              : "No archived events yet"}
          </p>
        </div>
      ) : (
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3">
          {eventsWithHosts?.map((event) => {
            const eventWithGroups = { ...event, groups: groupsByEvent.get(event.id) ?? [] };
            const eventTags = (event as any).tags || (event as any).event_tags?.map((t: any) => t.tag) || [];
            const memberDiscountMatch = getBestMembershipDiscount(memberDiscounts || [], eventTags);
            return (
              <EventCard 
                key={event.id} 
                event={eventWithGroups} 
                isBooked={bookedEventIds.includes(event.id)}
                isArchived={isArchived}
                adventPassEligibility={adventPassEligibility}
                adventPassData={adventPassData}
                membershipDiscount={memberDiscountMatch}
                onEdit={isAdmin ? onEditEvent : undefined}
              />
            );
          })}
        </div>
      )}
      </div>
    </>
  );
};

export default DynamicEventSection;
